import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import BillQuickList from './BillQuickList';
import { filterActiveAllocations } from './AllocationQuickList';

const PANEL_MAX_WIDTH = 300;
const VIEWPORT_MARGIN = 12;

/**
 * Pill + floating panel for included bills (same pattern as allocations).
 */
const IncludedBillsTrigger = ({
  bills,
  fortnightStart,
  fortnightEnd,
  accountTitle,
  inline = false,
}) => {
  const [open, setOpen] = useState(false);
  const [panelPosition, setPanelPosition] = useState(null);
  const wrapRef = useRef(null);
  const pillRef = useRef(null);
  const panelId = useId();
  const count = filterActiveAllocations(bills, fortnightStart, fortnightEnd).length;

  const updatePanelPosition = () => {
    if (!pillRef.current) return;
    const rect = pillRef.current.getBoundingClientRect();
    const panelWidth = Math.min(PANEL_MAX_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
    let left = rect.left;
    if (left + panelWidth > window.innerWidth - VIEWPORT_MARGIN) {
      left = window.innerWidth - VIEWPORT_MARGIN - panelWidth;
    }
    left = Math.max(VIEWPORT_MARGIN, left);

    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < 180 && rect.top > 180;

    setPanelPosition({
      top: openAbove ? undefined : rect.bottom + 6,
      bottom: openAbove ? window.innerHeight - rect.top + 6 : undefined,
      left,
      width: panelWidth,
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      setPanelPosition(null);
      return undefined;
    }
    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);
    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      const panel = document.getElementById(panelId);
      if (
        wrapRef.current?.contains(event.target) ||
        panel?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, panelId]);

  const toggle = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setOpen((prev) => !prev);
  };

  if (!bills.length) {
    return null;
  }

  const pillLabel = inline
    ? `${count} bill${count === 1 ? '' : 's'}`
    : `Included bills (${count})`;

  const panel =
    open &&
    panelPosition &&
    createPortal(
      <div
        id={panelId}
        className="requirements-included-allocations-panel requirements-included-allocations-panel--floating"
        role="dialog"
        aria-label="Included bills"
        style={{
          position: 'fixed',
          top: panelPosition.top,
          bottom: panelPosition.bottom,
          left: panelPosition.left,
          width: panelPosition.width,
          zIndex: 1080,
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="requirements-included-allocations-panel-title">Active this fortnight</div>
        <BillQuickList bills={bills} fortnightStart={fortnightStart} fortnightEnd={fortnightEnd} />
      </div>,
      document.body
    );

  const Wrap = inline ? 'span' : 'div';

  return (
    <Wrap
      className={`requirements-included-allocations-wrap${
        inline ? ' requirements-included-allocations-wrap--inline' : ''
      }`}
      ref={wrapRef}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        ref={pillRef}
        type="button"
        className={`requirements-included-allocations-pill${
          inline ? ' requirements-included-allocations-pill--inline' : ''
        }`}
        onClick={toggle}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-haspopup="dialog"
        aria-label={`Bills for ${accountTitle}`}
      >
        {pillLabel}
      </button>
      {panel}
    </Wrap>
  );
};

IncludedBillsTrigger.propTypes = {
  bills: PropTypes.arrayOf(PropTypes.object).isRequired,
  fortnightStart: PropTypes.instanceOf(Date).isRequired,
  fortnightEnd: PropTypes.instanceOf(Date).isRequired,
  accountTitle: PropTypes.string.isRequired,
  inline: PropTypes.bool,
};

export default IncludedBillsTrigger;
